import { supabase as defaultSupabase } from './supabaseClient.js';
import { v4 as uuidv4 } from 'uuid';

/**
 * Servicio para gestión de certificados
 */
export class CertificatesService {
  static supabase = defaultSupabase;

  /**
   * Genera un código único para el certificado
   */
  static generateCertificateCode(companyName, planName) {
    const timestamp = Date.now().toString().slice(-6);
    const companyCode = companyName.substring(0, 3).toUpperCase();
    const planCode = planName.substring(0, 3).toUpperCase();
    return `CERT-${companyCode}-${planCode}-${timestamp}`;
  }

  /**
   * Crea un certificado automáticamente desde un pago completado
   */
  static async createCertificateFromPayment(pagoId) {
    try {
      // Obtener información del pago y contratación
      const { data: pagoData, error: pagoError } = await this.supabase
        .from('pagos')
        .select(`
          *,
          contratacion!inner(
            id,
            estado,
            cotizacion!inner(
              id,
              plan!inner(
                id,
                nombre,
                producto!inner(
                  id,
                  nombre,
                  compania!inner(
                    id,
                    nombre
                  )
                )
              )
            )
          )
        `)
        .eq('id', pagoId)
        .single();

      if (pagoError) throw pagoError;

      if (!pagoData || pagoData.estado !== 'completado') {
        throw new Error('El pago debe estar completado para generar el certificado');
      }

      const contratacion = pagoData.contratacion;
      const plan = contratacion.cotizacion.plan;
      const producto = plan.producto;
      const compania = producto.compania;

      // Verificar si ya existe un certificado para este pago
      const { data: existingCert } = await this.supabase
        .from('certificados')
        .select('id')
        .eq('pago_id', pagoId)
        .single();

      if (existingCert) {
        throw new Error('Ya existe un certificado para este pago');
      }

      // Generar código del certificado
      const codigo = this.generateCertificateCode(compania.nombre, plan.nombre);

      // Calcular fechas de cobertura (ejemplo: 1 año desde la fecha de pago)
      const fechaEmision = new Date();
      const fechaInicioCobertura = new Date();
      const fechaTerminoCobertura = new Date();
      fechaTerminoCobertura.setFullYear(fechaTerminoCobertura.getFullYear() + 1);

      // Crear el certificado
      const { data: certificado, error: certError } = await this.supabase
        .from('certificados')
        .insert({
          id: uuidv4(),
          pago_id: pagoId,
          codigo,
          fecha_emision: fechaEmision.toISOString(),
          fecha_inicio_cobertura: fechaInicioCobertura.toISOString(),
          fecha_termino_cobertura: fechaTerminoCobertura.toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (certError) throw certError;

      // Actualizar el estado de la contratación a 'certificado_emitido'
      await this.supabase
        .from('contrataciones')
        .update({ 
          estado: 'certificado_emitido',
          updated_at: new Date().toISOString()
        })
        .eq('id', contratacion.id);

      return { data: certificado, error: null };
    } catch (error) {
      console.error('Error creando certificado desde pago:', error);
      return { data: null, error };
    }
  }

  /**
   * Procesa automáticamente la generación de certificados para pagos completados
   */
  static async processAutomaticCertificateGeneration() {
    try {
      // Buscar pagos completados sin certificado
      const { data: pagosCompletados, error: pagosError } = await this.supabase
        .from('pagos')
        .select(`
          id,
          contratacion!inner(
            id,
            estado
          )
        `)
        .eq('estado', 'completado')
        .eq('contratacion.estado', 'contratado');

      if (pagosError) throw pagosError;

      // Filtrar pagos que ya tienen certificado
      const { data: certificadosExistentes } = await this.supabase
        .from('certificados')
        .select('pago_id')
        .not('pago_id', 'is', null);

      const pagosConCertificado = new Set(
        certificadosExistentes?.map(c => c.pago_id) || []
      );

      const pagosSinCertificado = pagosCompletados?.filter(
        pago => !pagosConCertificado.has(pago.id)
      ) || [];

      const results = [];
      
      for (const pago of pagosSinCertificado) {
        const result = await this.createCertificateFromPayment(pago.id);
        results.push({
          pagoId: pago.id,
          success: !result.error,
          error: result.error?.message,
          certificado: result.data
        });
      }

      return { data: results, error: null };
    } catch (error) {
      console.error('Error en generación automática de certificados:', error);
      return { data: null, error };
    }
  }
  /**
   * Obtener certificados emitidos (correctamente emitidos = tienen subscription_id).
   *
   * Flujo de negocio:
   *   cotizacion → contratacion → subscription (confirmada) → certificado
   *
   * Query:
   *   certificados
   *     └─ subscription_id → subscriptions
   *           ├─ contratacion_id → contrataciones  (nombre, mail, rut)
   *           └─ company_id     → companias         (nombre, logo)
   */
  static async getCertificates(filters = {}, client = null) {
    const db = client || this.supabase;
    try {
      // Obtener certificados de company_certificados (tabla con vista agrupada)
      const { data: newCertsData, error: newCertsError } = await db
        .from('company_certificados')
        .select(`
          id,
          codigo,
          fecha_emision,
          fecha_inicio_cobertura,
          fecha_termino_cobertura,
          subscription_id,
          email_enviado,
          archivo_id,
          cliente_nombre,
          cliente_rut,
          cliente_email,
          compania_nombre,
          compania_rut,
          plan_nombre,
          producto_nombre,
          tipo_certificado,
          monto,
          subscription_status,
          subscription_codigo,
          orden_pago_id,
          pago_id,
          contratacion_id,
          contratacion_codigo,
          cotizacion_codigo,
          cotizacion_id,
          estado,
          cobertura_hasta,
          estado_emision,
          poliza_colectiva_id
        `)
        .order('fecha_emision', { ascending: false });

      if (newCertsError) {
        console.error('getCertificates error:', newCertsError.message);
        throw newCertsError;
      }

      // Obtener códigos de cotizaciones para certificados nuevos si falta cotizacion_codigo
      let quotationCodigosMap = new Map();
      let archivosMap = new Map();
      const cotizacionIds = [...new Set(
        newCertsData
          .filter(cert => cert.cotizacion_id)
          .map(cert => cert.cotizacion_id)
      )];

      // Obtener archivos para certificados nuevos (desde los archivoIds de company_certificados)
      const archivoIds = [...new Set(
        newCertsData
          .map(cert => cert.archivo_id)
          .filter(Boolean)
      )];

      if (archivoIds.length > 0) {
        const { data: archivosData } = await db
          .from('archivos_storage')
          .select('id, url, tipo')
          .in('id', archivoIds);

        if (archivosData) {
          archivosMap = new Map(
            archivosData.map(archivo => [archivo.id, archivo])
          );
        }
      }

      if (cotizacionIds.length > 0) {
        const { data: quotationData } = await db
          .from('company_cotizaciones')
          .select('id, codigo')
          .in('id', cotizacionIds);

        if (quotationData) {
          quotationCodigosMap = new Map(
            quotationData.map(q => [q.id, q.codigo])
          );
        }
      }

      // Normalizar datos de company_certificados
      const newCerts = (newCertsData || []).map(cert => {
        const archivoId = cert.archivo_id;
        const archivoInfo = archivosMap.get(archivoId);
        return {
          id: cert.id,
          codigo: cert.codigo,
          fecha_emision: cert.fecha_emision,
          fecha_inicio_cobertura: cert.fecha_inicio_cobertura,
          fecha_termino_cobertura: cert.fecha_termino_cobertura,
          subscription_id: cert.subscription_id,
          pago_id: cert.pago_id || null,
          orden_pago_id: cert.orden_pago_id || null,
          tipo_certificado: cert.tipo_certificado || 'suscripcion',
          archivo_id: archivoId,
          email_enviado: cert.email_enviado,
          archivo: archivoInfo ? { url: archivoInfo.url, tipo: archivoInfo.tipo } : null,
          source: 'new',
          productName: cert.producto_nombre || '—',
          planName: cert.plan_nombre || '—',
          subscription: {
            id: cert.subscription_id,
            status: cert.subscription_status || 'confirmada',
            amount: cert.monto,
            plan_name: cert.plan_nombre,
            company_id: null,
            contratacion_id: cert.contratacion_id,
            start_date: cert.fecha_inicio_cobertura,
            end_date: cert.fecha_termino_cobertura,
            contratacion: {
              id: cert.contratacion_id,
              nombre: cert.cliente_nombre,
              mail: cert.cliente_email,
              rut: cert.cliente_rut,
              codigo: cert.contratacion_codigo || null,
              contratacion_cargas: []
            },
            compania: {
              id: null,
              nombre_corto: cert.compania_nombre,
              razon_social: cert.compania_nombre,
              logo_url: null
            }
          },
          subscriptionCodigo: cert.subscription_codigo || '—',
          contractCodigo: cert.contratacion_codigo || '—',
          quotationCodigo: cert.cotizacion_codigo || quotationCodigosMap.get(cert.cotizacion_id) || '—',
          certificateCodigo: cert.codigo || '—',
          estadoCertificado: cert.estado || 'vigente',
          coberturaHasta: cert.cobertura_hasta || null,
          estadoEmision: cert.estado_emision || null,
          polizaColectivaId: cert.poliza_colectiva_id || null
        };
      });

      // Ordenar por fecha
      let allCerts = newCerts.sort((a, b) =>
        new Date(b.fecha_emision || 0) - new Date(a.fecha_emision || 0)
      );

      if (filters.search) {
        const q = filters.search.toLowerCase();
        allCerts = allCerts.filter(cert =>
          (cert.codigo || '').toLowerCase().includes(q) ||
          (cert.subscriptionCodigo || '').toLowerCase().includes(q) ||
          (cert.contractCodigo || '').toLowerCase().includes(q) ||
          (cert.subscription?.contratacion?.nombre || '').toLowerCase().includes(q) ||
          (cert.subscription?.contratacion?.rut || '').toLowerCase().includes(q)
        );
      }

      // Agrupar: subscription_id → por suscripción / sin subscription → por orden_pago_id
      const grouped = {};
      allCerts.forEach(cert => {
        const groupKey = cert.subscription_id
          ? `sub_${cert.subscription_id}`
          : `op_${cert.orden_pago_id || cert.pago_id || 'sin_orden'}`;

        if (!grouped[groupKey]) {
          grouped[groupKey] = {
            group_key: groupKey,
            group_tipo: cert.subscription_id ? 'suscripcion' : 'pago',
            subscription_codigo: cert.subscriptionCodigo !== '—' ? cert.subscriptionCodigo : null,
            subscription_id: cert.subscription_id,
            orden_pago_id: cert.orden_pago_id,
            certificates: []
          };
        }
        grouped[groupKey].certificates.push(cert);
      });

      // Convertir a array y ordenar por la fecha más reciente de cada grupo
      const groupedCerts = Object.values(grouped).sort((a, b) => {
        const dateA = new Date(a.certificates[0]?.fecha_emision || 0);
        const dateB = new Date(b.certificates[0]?.fecha_emision || 0);
        return dateB - dateA;
      });

      return groupedCerts;
    } catch (error) {
      console.error('Error fetching certificates:', error?.message || error);
      throw error;
    }
  }

  /**
   * Detalle completo de un certificado - Este código que sigue fue código muerto que necesitaba limpieza
   *
   * Misma cadena pero más profunda:
   *   subscription → contratacion → cotizacion → plan
   *                               → contratacion_cargas
   *                └─ compania   (logo, razón social)
   */
  /**
   * Detalle completo de un certificado.
   * Busca en company_certificados primero (con email_payload),
   * luego en certificados como fallback.
   */
  static async getCertificateDetail(id, client = null) {
    const db = client || this.supabase;
    try {
      // Intentar obtener de company_certificados primero (incluye email_payload)
      const { data: newCertData, error: newError } = await db
        .from('company_certificados')
        .select(`
          id,
          codigo,
          fecha_emision,
          fecha_inicio_cobertura,
          fecha_termino_cobertura,
          subscription_id,
          cotizacion_id,
          contratacion_id,
          archivo_id,
          email_enviado,
          email_payload
        `)
        .eq('id', id)
        .maybeSingle();

      if (newCertData) {
        let parsedEmailPayload = null;
        if (newCertData.email_payload) {
          if (typeof newCertData.email_payload === 'string') {
            parsedEmailPayload = JSON.parse(newCertData.email_payload);
          } else {
            parsedEmailPayload = newCertData.email_payload;
          }
        }
        return {
          ...newCertData,
          source: 'company_certificados',
          emailPayload: parsedEmailPayload,
        };
      }

      // Fallback a tabla antigua certificados
      const { data: oldCertData, error: oldError } = await db
        .from('certificados')
        .select(`
          id,
          codigo,
          fecha_emision,
          fecha_inicio_cobertura,
          fecha_termino_cobertura,
          subscription_id,
          pago_id,
          archivo_id,
          email_enviado,
          subscription:subscriptions!certificados_subscription_id_fkey(
            id,
            status,
            amount,
            plan_name,
            company_id,
            start_date,
            end_date,
            contratacion:contrataciones!subscriptions_contratacion_id_fkey(
              id,
              nombre,
              mail,
              rut,
              datos_titular,
              codigo,
              cotizacion:cotizaciones(
                id,
                planes:planes(
                  id,
                  nombre,
                  uf_base,
                  capital_uf,
                  descripcion,
                  precios_carga(tipo_carga, precio_uf, activo)
                ),
                cotizacion_lineas(
                  precio_uf,
                  descuento_aplicado
                )
              ),
              contratacion_cargas(
                id,
                nombre,
                apellido_paterno,
                apellido_materno,
                rut,
                tipo,
                edad
              )
            ),
            compania:companias(
              id,
              nombre_corto,
              razon_social,
              logo_url
            )
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (oldCertData) {
        return {
          ...oldCertData,
          source: 'certificados',
        };
      }

      if (newError) throw newError;
      if (oldError) throw oldError;

      return null;
    } catch (error) {
      console.error('Error fetching certificate detail:', error?.message || error);
      throw error;
    }
  }

  /**
   * Obtener un certificado por ID
   */
  static async getCertificateById(id) {
    try {
      const { data, error } = await this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!certificados_new_pago_id_fkey(
            id,
            amount,
            status,
            transaction_date,
            contratacion_id,
            orden_pago_id,
            contrataciones(
              id,
              rut,
              nombre,
              mail,
              estado,
              created_at,
              datos_titular
            ),
            ordenes_pago(
              id,
              monto,
              estado,
              fecha_expiracion
            )
          ),
          archivos_storage!certificados_new_archivo_id_fkey(
            id,
            bucket,
            path,
            url,
            tipo,
            nombre_original,
            created_at
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching certificate:', error);
      throw error;
    }
  }

  /**
   * Crear un nuevo certificado
   */
  static async createCertificate(certificateData) {
    try {
      if (!certificateData.subscription_id && !certificateData.pago_id) {
        throw new Error('subscription_id o pago_id es requerido');
      }

      const { data, error } = await this.supabase
        .from('certificados')
        .insert([{
          subscription_id: certificateData.subscription_id || null,
          pago_id:         certificateData.pago_id         || null,
          fecha_emision:   certificateData.fecha_emision   || new Date().toISOString(),
          archivo_id:      certificateData.archivo_id      || null,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating certificate:', error);
      throw error;
    }
  }

  /**
   * Actualizar un certificado
   */
  static async updateCertificate(id, updates) {
    try {
      // Solo permitir actualizar campos que existen en el esquema
      const allowedFields = ['fecha_emision', 'archivo_id'];
      const filteredUpdates = Object.keys(updates)
        .filter(key => allowedFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = updates[key];
          return obj;
        }, {});

      const { data, error } = await this.supabase
        .from('certificados')
        .update(filteredUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating certificate:', error);
      throw error;
    }
  }

  /**
   * Eliminar un certificado (no hay concepto de revocación en el esquema actual)
   */
  static async deleteCertificate(id) {
    try {
      const { data, error } = await this.supabase
        .from('certificados')
        .delete()
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error revoking certificate:', error);
      throw error;
    }
  }

  /**
   * Regenerar un certificado (actualizar el existente con nueva fecha de emisión)
   */
  static async regenerateCertificate(certificateId) {
    try {
      console.log('[regenerateCertificate] Starting with id:', certificateId);

      const res = await fetch('/api/certificates/regenerate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ certificateId }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Backend error ${res.status}`);
      }

      const { data: updatedCert } = await res.json();
      console.log('[regenerateCertificate] Certificate updated:', { id: updatedCert.id, codigo: updatedCert.codigo });
      return updatedCert;
    } catch (error) {
      console.error('Error regenerating certificate:', error);
      throw error;
    }
  }

  /**
   * Obtener certificados recientes (últimos N días)
   */
  static async getRecentCertificates(daysBack = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const { data, error } = await this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!certificados_new_pago_id_fkey(
            id,
            amount,
            status,
            transaction_date,
            contrataciones(
              id,
              nombre,
              mail,
              rut
            )
          )
        `)
        .gte('fecha_emision', startDate.toISOString())
        .order('fecha_emision', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching recent certificates:', error);
      throw error;
    }
  }

  /**
   * Validar un certificado por ID
   */
  static async validateCertificate(certificateId) {
    try {
      const { data, error } = await this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!certificados_new_pago_id_fkey(
            id,
            amount,
            status,
            transaction_date,
            contrataciones(
              id,
              nombre,
              mail,
              rut
            )
          ),
          archivos_storage!certificados_new_archivo_id_fkey(
            id,
            url,
            tipo
          )
        `)
        .eq('id', certificateId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return { valid: false, message: 'Certificado no encontrado' };
        }
        throw error;
      }

      // Verificar si el pago asociado está completado
      const paymentCompleted = data.pagos?.status === 'completed';
      
      if (!paymentCompleted) {
        return { 
          valid: false, 
          message: 'Certificado no válido - pago no completado',
          certificate: data
        };
      }

      return { 
        valid: true, 
        message: 'Certificado válido',
        certificate: data
      };
    } catch (error) {
      console.error('Error validating certificate:', error);
      throw error;
    }
  }

  /**
   * Obtener estadísticas de certificados
   */
  static async getCertificateStats() {
    try {
      const { data, error } = await this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!inner(status, transaction_date)
        `);

      if (error) throw error;

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const stats = {
        total_certificates: data.length,
        this_month: data.filter(c => 
          new Date(c.created_at) >= thisMonth
        ).length,
        last_month: data.filter(c => {
          const createdDate = new Date(c.created_at);
          return createdDate >= lastMonth && createdDate < thisMonth;
        }).length,
        by_payment_status: data.reduce((acc, cert) => {
          const status = cert.pagos?.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {})
      };

      return stats;
    } catch (error) {
      console.error('Error fetching certificate stats:', error);
      throw error;
    }
  }

  /**
   * Obtener certificados por contratación
   */
  static async getCertificatesByContract(contractId) {
    try {
      const { data, error } = await this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!certificados_new_pago_id_fkey(
            id,
            amount,
            status,
            transaction_date,
            contrataciones(
              id,
              nombre,
              mail,
              rut
            )
          ),
          archivos_storage!certificados_new_archivo_id_fkey(
            id,
            url,
            tipo
          )
        `)
        .eq('pagos.contrataciones.id', contractId)
        .order('fecha_emision', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching certificates by contract:', error);
      throw error;
    }
  }

  /**
   * Generar reporte de certificados
   */
  static async generateCertificateReport(filters = {}) {
    try {
      let query = this.supabase
        .from('certificados')
        .select(`
          *,
          pagos!certificados_new_pago_id_fkey(
            id,
            amount,
            status,
            transaction_date,
            contrataciones(
              id,
              nombre,
              mail,
              rut
            )
          ),
          archivos_storage!certificados_new_archivo_id_fkey(
            id,
            url,
            tipo
          )
        `);

      // Aplicar filtros del reporte
      if (filters.date_from) {
        query = query.gte('fecha_emision', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('fecha_emision', filters.date_to);
      }
      if (filters.payment_status) {
        query = query.eq('pagos.status', filters.payment_status);
      }
      if (filters.client_name) {
        query = query.ilike('pagos.contrataciones.nombre', `%${filters.client_name}%`);
      }

      const { data, error } = await query.order('fecha_emision', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error generating certificate report:', error);
      throw error;
    }
  }
  // ─── PDF generation via Playwright ───────────────────────────────────────

  /**
   * Calls POST /api/pdf/generate with action "upload_and_email".
   * Generates the PDF on the server, uploads it to Supabase Storage, and
   * sends it as an attachment to the given email address.
   *
   * @param {string} certificadoId
   * @param {string} emailTo
   * @param {string} [emailSubject]
   * @returns {Promise<{ ok: boolean, url: string, archivo_id: string }>}
   */
  static async generateAndEmailPdf(certificadoId, emailTo, emailSubject) {
    const res = await fetch('/api/certificates/send-email', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
      },
      body: JSON.stringify({
        certificateId: certificadoId,
        emailTo,
        emailSubject: emailSubject || undefined,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `Email sending failed: ${res.status}`);
    }
    return res.json();
  }

  /**
   * Calls POST /api/pdf/generate with action "download".
   * The browser will receive the raw PDF bytes and trigger a file download.
   *
   * @param {string} certificadoId
   * @param {string} [filename]   defaults to "certificado-<id>.pdf"
   */
  static async downloadCertificatePdf(certificadoId, filename) {
    const secret = process.env.REACT_APP_PDF_SECRET
      || process.env.REACT_APP_CRON_SECRET
      || '';

    const res = await fetch('/api/pdf/generate', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${secret}`,
      },
      body: JSON.stringify({
        type:   'certificado',
        id:     certificadoId,
        action: 'download',
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `PDF download failed: ${res.status}`);
    }

    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename || `certificado-${certificadoId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  /**
   * Uploads the PDF to Supabase Storage (without emailing it).
   * Updates certificados.archivo_id on success.
   *
   * @param {string} certificadoId
   * @returns {Promise<{ ok: boolean, url: string, archivo_id: string }>}
   */
  static async uploadCertificatePdf(certificadoId) {
    const secret = process.env.REACT_APP_PDF_SECRET
      || process.env.REACT_APP_CRON_SECRET
      || '';

    const res = await fetch('/api/pdf/generate', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${secret}`,
      },
      body: JSON.stringify({
        type:   'certificado',
        id:     certificadoId,
        action: 'upload',
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `PDF upload failed: ${res.status}`);
    }
    return res.json();
  }

  /**
   * Generates PDF del certificado, lo combina con el condicionado del plan y lo sube a storage
   * @param {string} certificadoId
   * @returns {Promise<{ ok: boolean, url: string, archivo_id: string }>}
   */
  static async regenerateCertificateWithCondicionado(certificadoId) {
    const secret = process.env.REACT_APP_PDF_SECRET
      || process.env.REACT_APP_CRON_SECRET
      || '';

    const res = await fetch('/api/pdf/generate', {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${secret}`,
      },
      body: JSON.stringify({
        type:   'certificado',
        id:     certificadoId,
        action: 'upload_with_condicionado',
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error || `PDF generation with condicionado failed: ${res.status}`);
    }
    return res.json();
  }
}

export default CertificatesService;
